// Per-PSP credential schemas — kept in lockstep with
// app/services/gateway_config.py::_SCHEMAS on the backend.
//
// The form layer reads `fields` to render inputs and uses the Zod schema for
// client-side validation. The backend is the source of truth (it re-validates
// on PUT /admin/merchants/{id}/gateways/{psp}); this is purely UX so the user
// gets instant feedback.

import { z } from 'zod';

import type { PspCode } from '@/api/types';

export type FieldKind = 'text' | 'secret' | 'number' | 'select';

export interface FieldDef {
  name: string;
  label: string;
  kind: FieldKind;
  placeholder?: string;
  help?: string;
  options?: readonly string[];
}

export interface PspFormDef {
  schema: z.ZodTypeAny;
  fields: readonly FieldDef[];
  defaults: Record<string, unknown>;
}

const sadad = {
  schema: z.object({
    auth_token: z.string().min(8, 'At least 8 characters'),
    category: z.coerce.number().int().min(1).max(99).default(20),
  }),
  fields: [
    { name: 'auth_token', label: 'Auth token', kind: 'secret' as const, help: 'Bearer token issued by Almadar.' },
    { name: 'category', label: 'Category', kind: 'number' as const, placeholder: '20' },
  ],
  defaults: { category: 20 },
} satisfies PspFormDef;

const moamalat = {
  schema: z.object({
    merchant_id: z.string().min(1),
    terminal_id: z.string().min(1),
    secret_key_hex: z.string().regex(/^[0-9a-fA-F]+$/, 'Hex string').min(8),
    currency_code: z.string().regex(/^\d{3}$/).default('434'),
    lightbox_env: z.enum(['test', 'production']).default('test'),
  }),
  fields: [
    { name: 'merchant_id', label: 'Merchant ID', kind: 'text' as const },
    { name: 'terminal_id', label: 'Terminal ID', kind: 'text' as const },
    {
      name: 'secret_key_hex',
      label: 'Secret key (hex)',
      kind: 'secret' as const,
      help: 'Decoded with bytes.fromhex on the backend — must be valid hex.',
    },
    { name: 'currency_code', label: 'Currency (ISO numeric)', kind: 'text' as const, placeholder: '434' },
    {
      name: 'lightbox_env',
      label: 'LightBox environment',
      kind: 'select' as const,
      options: ['test', 'production'] as const,
    },
  ],
  defaults: { currency_code: '434', lightbox_env: 'test' },
} satisfies PspFormDef;

const mobicash = {
  schema: z.object({
    merchant_id: z.string().min(1),
    api_key: z.string().min(8),
    api_secret: z.string().optional().or(z.literal('')),
  }),
  fields: [
    { name: 'merchant_id', label: 'Merchant ID', kind: 'text' as const },
    { name: 'api_key', label: 'API key', kind: 'secret' as const },
    {
      name: 'api_secret',
      label: 'API secret (optional)',
      kind: 'secret' as const,
      help: 'If set, webhooks must include a matching X-Mobicash-Signature.',
    },
  ],
  defaults: {},
} satisfies PspFormDef;

const tlync = {
  schema: z.object({
    store_id: z.string().min(1),
    api_key: z.string().min(8),
    backend_url: z.string().url(),
    frontend_url: z.string().url(),
    callback_secret: z.string().optional().or(z.literal('')),
  }),
  fields: [
    { name: 'store_id', label: 'Store ID', kind: 'text' as const },
    { name: 'api_key', label: 'API key', kind: 'secret' as const },
    {
      name: 'backend_url',
      label: 'Backend (callback) URL',
      kind: 'text' as const,
      placeholder: 'https://hub.example/v1/callbacks/tlync/<merchant_id>',
    },
    {
      name: 'frontend_url',
      label: 'Frontend redirect URL',
      kind: 'text' as const,
      placeholder: 'https://merchant.example/return',
    },
    {
      name: 'callback_secret',
      label: 'Callback secret (optional)',
      kind: 'secret' as const,
      help: 'Production should always set this.',
    },
  ],
  defaults: {},
} satisfies PspFormDef;

const adfali = {
  schema: z.object({
    merchant_id: z.string().optional().or(z.literal('')),
    api_key: z.string().optional().or(z.literal('')),
  }),
  fields: [
    { name: 'merchant_id', label: 'Merchant ID', kind: 'text' as const },
    { name: 'api_key', label: 'API key', kind: 'secret' as const },
  ],
  defaults: {},
} satisfies PspFormDef;

export const PSP_SCHEMAS: Record<PspCode, PspFormDef> = {
  sadad,
  moamalat,
  mobicash,
  tlync,
  adfali,
};

export const ALL_PSPS: PspCode[] = ['sadad', 'moamalat', 'mobicash', 'tlync', 'adfali'];

// Brand artwork for each adapter.
export const PSP_LOGOS: Record<PspCode, string | null> = {
  sadad: '/psp/sadad.png',
  moamalat: '/psp/moamalat.png',
  mobicash: '/psp/mobicash.png',
  tlync: '/psp/tlync.png',
  adfali: '/psp/adfali.png',
};
